import {
  ConfirmationDocument,
  ConfirmationImmutabilityReason,
  useVbsConfirmationPdfDownloadMutation,
  useVbsConfirmationPdfPreviewQuery,
  useVbsConfirmationSetImmutableMutation,
} from "@/services/backend/vbs/confirmations/api/confirmation-api";
import {
  rtkErrIsErrDocumentNotReady,
  useErrDocumentNotReady,
  useSetErrDocumentNotReadyFromRtk,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import { Separator } from "@/shared/components/ui/separator";
import {
  DocumentNotReadyAlert,
  DocumentSheet,
  DocumentSheetActions,
  DocumentSheetActionsContent,
  DocumentSheetActionsDownload,
  DocumentSheetActionsHeader,
  DocumentSheetActionsSetImmutableMutation,
  DocumentSheetContent,
  DocumentSheetHeader,
  DocumentSheetImmutabilityWarning,
  DocumentSheetMain,
  DocumentSheetPreview,
  DocumentSheetTitle,
  DocumentSheetTrigger,
} from "@/shared/bsm/core/document/components/ui/document-sheet";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Download } from "lucide-react";
import { useDownloadPdf } from "@/shared/lib/utilities/download-pdf";
import { useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function ConfirmationPdf({
  confirmationId,
  immutable = false,
}: {
  confirmationId: string;
  immutable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, error } = useVbsConfirmationPdfPreviewQuery(
    {
      id: confirmationId,
      document: ConfirmationDocument.All,
    },
    {
      skip: !open,
    },
  );

  const { notReadyError, setNotReadyError } = useErrDocumentNotReady();
  const { user } = useAuth();

  return (
    <DocumentSheet open={open} onOpenChange={setOpen}>
      <DocumentSheetTrigger asChild>
        <Button variant="outline">PDF</Button>
      </DocumentSheetTrigger>
      <DocumentSheetContent className="max-h-[90vh]">
        <DocumentSheetHeader>
          <DocumentSheetTitle>{t("Download PDF")}</DocumentSheetTitle>
        </DocumentSheetHeader>
        <DocumentSheetMain>
          <DocumentSheetPreview
            objectURL={data?.objectURL}
            isLoading={isLoading}
            error={error}
          />
          <DocumentSheetActions>
            <DocumentSheetActionsHeader
              title={t("Auftragsbestätigung")}
              documentNumber={
                <DocumentNumberById documentId={confirmationId} />
              }
            />
            <DocumentSheetActionsContent>
              {!immutable && (
                <>
                  <DocumentNotReadyAlert
                    error={notReadyError}
                    document={t("Auftragsbestätigung")}
                  />
                  <DocumentSheetImmutabilityWarning
                    title={t("Hinweis")}
                    description={t(
                      "Um eine Nummer zu generieren, schreiben Sie die Auftragsbestätigung fest.",
                    )}
                  />
                  <DocumentSheetActionsSetImmutableMutation
                    request={{
                      id: confirmationId,
                      reason: ConfirmationImmutabilityReason.SetByUser,
                      setBy: user?.id || "",
                    }}
                    mutation={useVbsConfirmationSetImmutableMutation}
                    description={t(
                      "Die Auftragsbestätigung wird festgeschrieben und es wird eine Nummer generiert. Danach kann die Auftragsbestätigung nicht mehr geändert werden.",
                    )}
                    onError={(err) => {
                      if (rtkErrIsErrDocumentNotReady(err)) {
                        setNotReadyError(err.data);
                      }
                    }}
                  />
                  <Separator />
                </>
              )}
              <DocumentSheetActionsDownload>
                <DownloadDocumentButton
                  confirmationId={confirmationId}
                  document={ConfirmationDocument.All}
                  text={t("Alle Dokumente")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmationId}
                  document={ConfirmationDocument.Letter}
                  text={t("Bestätigung")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmationId}
                  document={ConfirmationDocument.Treatment}
                  text={t("Leistungsverzeichnis")}
                />
                <DownloadDocumentButton
                  confirmationId={confirmationId}
                  document={ConfirmationDocument.TreatmentByArea}
                  text={t("Bereichsaufteilung")}
                />
              </DocumentSheetActionsDownload>
            </DocumentSheetActionsContent>
          </DocumentSheetActions>
        </DocumentSheetMain>
      </DocumentSheetContent>
    </DocumentSheet>
  );
}

function DownloadDocumentButton({
  confirmationId,
  document: documentType,
  text,
}: {
  confirmationId: string;
  document: ConfirmationDocument;
  text: string;
}) {
  const [open, setOpen] = useState(false);
  const [download, { isLoading, error, data, reset }] =
    useVbsConfirmationPdfDownloadMutation();

  useDownloadPdf(data, () => {
    setOpen(false);
    reset();
  });

  useSetErrDocumentNotReadyFromRtk(error, () => {
    setOpen(false);
    reset();
  });

  const doDownload = () => {
    if (isLoading) {
      return;
    }

    download({ id: confirmationId, document: documentType });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="justify-start">
          <Download className="mr-4 h-5 w-5" /> {text}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Auftragsbestätigung herunterladen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Wenn eine Auftragsbestätigung heruntergeladen wird, wird sie festgeschrieben. Sie kann dann nicht mehr geändert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doDownload} disabled={isLoading}>
            {isLoading ? (
              <Download className="mr-4 h-5 w-5 animate-bounce" />
            ) : (
              <Download className="mr-4 h-5 w-5" />
            )}
            <span>{t("Herunterladen")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
